import { backupSchema, validateBackupRelations, type BackupRelationIssue } from '../domain/schemas';
import type { BackupData, BackupSchema, RecoverySnapshot } from '../domain/types';
import { db, initializeDatabase, type WaschPilotDatabase } from './database';
import { allSchoolTables, loadAllSchoolData } from './repositories';
import {
  BACKUP_SCHEMA_VERSION,
  DATABASE_SCHEMA_VERSION,
  SEED_RECOMMENDATION_RULES,
  SEED_STAIN_GUIDES,
  SEED_TEMPERATURE_GUIDANCE,
} from './seed';

const APP_VERSION = '2.0.0';
const MAX_RECOVERY_SNAPSHOTS = 3;

export type BackupCounts = Record<keyof BackupData, number>;

export interface BackupPreview {
  valid: boolean;
  backup: BackupSchema | null;
  originalDatabaseSchemaVersion: number | null;
  exportedAt: string | null;
  counts: BackupCounts | null;
  warnings: string[];
  issues: string[];
}

export interface ImportBackupResult {
  backup: BackupSchema;
  preImportBackup: BackupSchema;
}

export class InvalidBackupError extends Error {
  constructor(
    message: string,
    public readonly issues: readonly string[],
  ) {
    super(message);
    this.name = 'InvalidBackupError';
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function decodeSource(source: unknown): unknown {
  if (typeof source !== 'string') return structuredClone(source);
  try {
    return JSON.parse(source) as unknown;
  } catch {
    throw new InvalidBackupError('The backup is not valid JSON.', ['invalidJson']);
  }
}

function migrateLegacyBackup(source: unknown): {
  migrated: unknown;
  originalVersion: number | null;
  warnings: string[];
} {
  if (!isRecord(source)) return { migrated: source, originalVersion: null, warnings: [] };
  const originalVersion =
    typeof source.databaseSchemaVersion === 'number' ? source.databaseSchemaVersion : null;
  if (
    originalVersion === null ||
    originalVersion >= DATABASE_SCHEMA_VERSION ||
    !isRecord(source.data)
  ) {
    return { migrated: source, originalVersion, warnings: [] };
  }

  const migrated = structuredClone(source);
  if (!isRecord(migrated) || !isRecord(migrated.data)) {
    return { migrated: source, originalVersion, warnings: [] };
  }
  const data = migrated.data;
  const products = Array.isArray(data.products)
    ? data.products.filter(isRecord).filter((product) => typeof product.id === 'string')
    : [];
  const productIds = new Set(products.map((product) => product.id as string));

  if (originalVersion < 2) {
    data.conversionConfigurations = products.map((product) => ({
      productId: product.id,
      schoolGramsPerMl: 1,
      calibratedGramsPerMl: null,
      activeConversionMode: 'school',
      calibrationStatus: 'notCalibrated',
      calibrationDate: null,
      calibrationNote: '',
      updatedAt: new Date().toISOString(),
    }));
    data.calibrationMeasurements = [];
  }

  if (originalVersion < 3) {
    data.temperatureGuidance = structuredClone(
      SEED_TEMPERATURE_GUIDANCE.filter((entry) => productIds.has(entry.productId)),
    );
    data.recommendationRules = structuredClone(
      SEED_RECOMMENDATION_RULES.filter(
        (entry) => entry.productId === null || productIds.has(entry.productId),
      ),
    );
    data.stainGuides = structuredClone(
      SEED_STAIN_GUIDES.map((entry) => ({
        ...entry,
        recommendedProductId:
          entry.recommendedProductId !== null && productIds.has(entry.recommendedProductId)
            ? entry.recommendedProductId
            : null,
      })),
    );
  }

  migrated.databaseSchemaVersion = DATABASE_SCHEMA_VERSION;
  return {
    migrated,
    originalVersion,
    warnings: [`migratedDatabaseSchema:${originalVersion}->${DATABASE_SCHEMA_VERSION}`],
  };
}

function countsFor(data: BackupData): BackupCounts {
  return {
    settings: data.settings.length,
    products: data.products.length,
    dosageTables: data.dosageTables.length,
    waterHardness: data.waterHardness.length,
    containers: data.containers.length,
    measuringCups: data.measuringCups.length,
    conversionConfigurations: data.conversionConfigurations.length,
    calibrationMeasurements: data.calibrationMeasurements.length,
    temperatureGuidance: data.temperatureGuidance.length,
    recommendationRules: data.recommendationRules.length,
    stainGuides: data.stainGuides.length,
  };
}

function relationIssueText(issue: BackupRelationIssue): string {
  return `${issue.code}:${issue.path}`;
}

export function previewBackup(source: unknown): BackupPreview {
  let decoded: unknown;
  try {
    decoded = decodeSource(source);
  } catch (error) {
    return {
      valid: false,
      backup: null,
      originalDatabaseSchemaVersion: null,
      exportedAt: null,
      counts: null,
      warnings: [],
      issues: error instanceof InvalidBackupError ? [...error.issues] : ['invalidJson'],
    };
  }

  if (isRecord(decoded)) {
    if (
      typeof decoded.backupSchemaVersion === 'number' &&
      decoded.backupSchemaVersion > BACKUP_SCHEMA_VERSION
    ) {
      return {
        valid: false,
        backup: null,
        originalDatabaseSchemaVersion:
          typeof decoded.databaseSchemaVersion === 'number' ? decoded.databaseSchemaVersion : null,
        exportedAt: typeof decoded.exportedAt === 'string' ? decoded.exportedAt : null,
        counts: null,
        warnings: [],
        issues: ['newerBackupSchema'],
      };
    }
    if (
      typeof decoded.databaseSchemaVersion === 'number' &&
      decoded.databaseSchemaVersion > DATABASE_SCHEMA_VERSION
    ) {
      return {
        valid: false,
        backup: null,
        originalDatabaseSchemaVersion: decoded.databaseSchemaVersion,
        exportedAt: typeof decoded.exportedAt === 'string' ? decoded.exportedAt : null,
        counts: null,
        warnings: [],
        issues: ['newerDatabaseSchema'],
      };
    }
  }

  const migration = migrateLegacyBackup(decoded);
  const parsed = backupSchema.safeParse(migration.migrated);
  if (!parsed.success) {
    return {
      valid: false,
      backup: null,
      originalDatabaseSchemaVersion: migration.originalVersion,
      exportedAt:
        isRecord(decoded) && typeof decoded.exportedAt === 'string' ? decoded.exportedAt : null,
      counts: null,
      warnings: migration.warnings,
      issues: parsed.error.issues.map(
        (issue) => `${issue.code}:${issue.path.map(String).join('.') || 'root'}`,
      ),
    };
  }

  const relationIssues = validateBackupRelations(parsed.data.data);
  return {
    valid: relationIssues.length === 0,
    backup: relationIssues.length === 0 ? parsed.data : null,
    originalDatabaseSchemaVersion: migration.originalVersion,
    exportedAt: parsed.data.exportedAt,
    counts: countsFor(parsed.data.data),
    warnings: migration.warnings,
    issues: relationIssues.map(relationIssueText),
  };
}

export async function createBackup(
  database: WaschPilotDatabase = db,
  exportedAt = new Date().toISOString(),
): Promise<BackupSchema> {
  const data = await loadAllSchoolData(database);
  return backupSchema.parse({
    format: 'waschpilot-school-backup',
    backupSchemaVersion: BACKUP_SCHEMA_VERSION,
    databaseSchemaVersion: DATABASE_SCHEMA_VERSION,
    appVersion: APP_VERSION,
    exportedAt,
    data,
  });
}

export async function exportBackupJson(database: WaschPilotDatabase = db): Promise<string> {
  return JSON.stringify(await createBackup(database), null, 2);
}

async function writeBackupData(
  database: WaschPilotDatabase,
  data: BackupData,
  snapshot: RecoverySnapshot,
): Promise<void> {
  const tables = allSchoolTables(database);
  await database.transaction('rw', [...tables, database.recoverySnapshots], async () => {
    await database.recoverySnapshots.add(snapshot);
    await Promise.all(tables.map((table) => table.clear()));
    await database.settings.bulkPut(data.settings);
    await database.products.bulkPut(data.products);
    await database.dosageTables.bulkPut(data.dosageTables);
    await database.waterHardness.bulkPut(data.waterHardness);
    await database.containers.bulkPut(data.containers);
    await database.measuringCups.bulkPut(data.measuringCups);
    await database.conversionConfigurations.bulkPut(data.conversionConfigurations);
    await database.calibrationMeasurements.bulkPut(data.calibrationMeasurements);
    await database.temperatureGuidance.bulkPut(data.temperatureGuidance);
    await database.recommendationRules.bulkPut(data.recommendationRules);
    await database.stainGuides.bulkPut(data.stainGuides);

    const obsoleteKeys = await database.recoverySnapshots
      .orderBy('id')
      .reverse()
      .offset(MAX_RECOVERY_SNAPSHOTS)
      .primaryKeys();
    await database.recoverySnapshots.bulkDelete(obsoleteKeys);
  });
}

export async function importBackup(
  source: unknown,
  database: WaschPilotDatabase = db,
): Promise<ImportBackupResult> {
  const preview = previewBackup(source);
  if (!preview.valid || preview.backup === null) {
    throw new InvalidBackupError('The backup cannot be imported.', preview.issues);
  }

  await initializeDatabase(database);
  const preImportBackup = await createBackup(database);
  await writeBackupData(database, preview.backup.data, {
    createdAt: new Date().toISOString(),
    reason: 'preImport',
    payload: preImportBackup,
  });
  return { backup: preview.backup, preImportBackup };
}

export async function listRecoverySnapshots(
  database: WaschPilotDatabase = db,
): Promise<RecoverySnapshot[]> {
  await initializeDatabase(database);
  return database.recoverySnapshots.orderBy('id').reverse().toArray();
}

export async function restoreRecoverySnapshot(
  snapshotId: number,
  database: WaschPilotDatabase = db,
): Promise<ImportBackupResult> {
  await initializeDatabase(database);
  const snapshot = await database.recoverySnapshots.get(snapshotId);
  if (!snapshot) {
    throw new InvalidBackupError('The recovery snapshot does not exist.', ['missingSnapshot']);
  }
  return importBackup(snapshot.payload, database);
}

export const backupService = {
  create: createBackup,
  exportJson: exportBackupJson,
  preview: previewBackup,
  import: importBackup,
  listRecoverySnapshots,
  restoreRecoverySnapshot,
};
